import React from 'react';
import PageLayout from '../../../../components/base/PageLayout';
const DressCode: React.FC = () => {
  React.useEffect(() => {
    document.title = 'ドレスコードの基本 - 執事が解説するTPO別の正しい服装選び｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが教えるドレスコードの完全ガイド。フォーマル、セミフォーマル、スマートカジュアル、ビジネスアタイアなど、TPOに応じた正しい服装の選び方を詳しく解説。スーツの着こなし、ネクタイの選び方、靴とベルトの合わせ方、アクセサリーのルールまで、紳士として恥ずかしくない装いの知識を網羅。結婚式、パーティー、ビジネスシーンで迷わない服装術をお伝えします。');
    }
  }, []);


  return (
    <PageLayout hero={<>
<div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: `url('/images/generated/seq-dress-hero-69c366.jpg')`
            }}
          />
          <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-serif mb-6 tracking-wide">
              ドレスコードの基本
            </h1>
            <p className="text-xl md:text-2xl font-light italic opacity-90">
              The Fundamentals of Dress Code
            </p>
          </div>
        </div>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-600">
              <p className="text-lg leading-relaxed text-gray-800 mb-0">
                適切な装いは、その人の品格と教養を表す重要な要素です。ドレスコードを理解し、場面に応じた服装を選ぶことで、相手への敬意を示し、自信を持って振る舞うことができます。執事として培った経験から、フォーマルな場面での装いの基本をお伝えいたします。
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  フォーマルウェアの基本
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      フォーマルな場面では、伝統的なルールに従った装いが求められます。男性の場合、最も格式高いのは燕尾服（テールコート）、次にタキシード、そしてダークスーツの順になります。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      女性の場合は、イブニングドレスやカクテルドレスが基本となり、時間帯や会場の格式に応じて選択します。肌の露出度や丈の長さにも配慮が必要です。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-dress-formal-ad7570.jpg"
                      alt="フォーマルウェアの基本"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  男性のドレスコード
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  男性のフォーマルウェアには明確な階層があり、それぞれに適した場面があります。正しい知識を身につけることで、どのような場面でも適切な装いを選ぶことができます。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="ri-vip-crown-line text-amber-600 mr-2"></i>
                      ホワイトタイ（燕尾服）
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">着用場面</h4>
                        <ul className="text-gray-700 space-y-1">
                          <li>• 国家的な式典</li>
                          <li>• 最高級のガラディナー</li>
                          <li>• オペラのプレミア</li>
                          <li>• 王室関連の行事</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">構成要素</h4>
                        <ul className="text-gray-700 space-y-1">
                          <li>• 黒の燕尾服</li>
                          <li>• 白のピケベスト</li>
                          <li>• 白の蝶ネクタイ</li>
                          <li>• エナメルの靴</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="ri-star-line text-amber-600 mr-2"></i>
                      ブラックタイ（タキシード）
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">着用場面</h4>
                        <ul className="text-gray-700 space-y-1">
                          <li>• 結婚式の披露宴</li>
                          <li>• 高級レストランでのディナー</li>
                          <li>• チャリティーガラ</li>
                          <li>• 企業の重要なパーティー</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">構成要素</h4>
                        <ul className="text-gray-700 space-y-1">
                          <li>• 黒のタキシードジャケット</li>
                          <li>• 黒のトラウザーズ</li>
                          <li>• 黒の蝶ネクタイ</li>
                          <li>• カマーバンドまたはベスト</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="ri-briefcase-line text-amber-600 mr-2"></i>
                      ダークスーツ
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">着用場面</h4>
                        <ul className="text-gray-700 space-y-1">
                          <li>• ビジネスディナー</li>
                          <li>• 昼間の結婚式</li>
                          <li>• 重要な商談</li>
                          <li>• 文化的なイベント</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">構成要素</h4>
                        <ul className="text-gray-700 space-y-1">
                          <li>• ネイビーまたはチャコールグレー</li>
                          <li>• 白または淡いブルーのシャツ</li>
                          <li>• シルクのネクタイ</li>
                          <li>• 革靴（黒または茶色）</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  女性のドレスコード
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-dress-women-be4798.jpg"
                      alt="女性のフォーマルウェア"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      女性のフォーマルウェアは、時間帯と場面の格式によって選択が決まります。昼間の正装と夜の正装では、大きく異なるルールがあります。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      色彩、素材、アクセサリーの選択も重要で、全体のバランスを考慮した上品な装いを心がけることが大切です。
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">女性フォーマルウェアのガイドライン</h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4">
                        <i className="ri-sun-line text-amber-600 mr-2"></i>
                        昼間の正装
                      </h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 膝丈またはそれ以下の丈</li>
                        <li>• 袖ありまたは適度な肌の露出</li>
                        <li>• 落ち着いた色合い</li>
                        <li>• パールなどの上品なアクセサリー</li>
                        <li>• クローズドトゥの靴</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4">
                        <i className="ri-moon-line text-amber-600 mr-2"></i>
                        夜の正装
                      </h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• ロングドレスまたはカクテルドレス</li>
                        <li>• 適度なデコルテの露出可</li>
                        <li>• 華やかな色彩も可</li>
                        <li>• ダイヤモンドなどの宝石類</li>
                        <li>• エレガントなヒール</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  アクセサリーと小物の選び方
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  アクセサリーは装いを完成させる重要な要素です。しかし、過度な装飾は品格を損ねる可能性があります。「Less is more」の精神で、質の高いものを厳選して身につけることが大切です。
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      <i className="ri-time-line text-amber-600 mr-2"></i>
                      時計
                    </h3>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• 薄型のドレスウォッチ</li>
                      <li>• 革ベルトまたは金属ベルト</li>
                      <li>• 控えめなデザイン</li>
                      <li>• 服装との調和を重視</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      <i className="ri-handbag-line text-amber-600 mr-2"></i>
                      バッグ
                    </h3>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• 小ぶりなクラッチバッグ</li>
                      <li>• 上質な革または布製</li>
                      <li>• 装いと同系色</li>
                      <li>• 必要最小限の中身</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      <i className="ri-footprint-line text-amber-600 mr-2"></i>
                      靴
                    </h3>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• 手入れの行き届いた状態</li>
                      <li>• 装いに合った色とスタイル</li>
                      <li>• 歩きやすい高さのヒール</li>
                      <li>• 上質な素材</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  季節と場所への配慮
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  ドレスコードは季節や開催場所によっても調整が必要です。屋外でのイベント、異なる気候、文化的な背景なども考慮に入れて装いを選ぶことが重要です。
                </p>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-800 mb-4">
                      <i className="ri-temp-cold-line mr-2"></i>
                      寒い季節の配慮
                    </h3>
                    <ul className="space-y-2 text-blue-700">
                      <li>• エレガントなコートの選択</li>
                      <li>• 上質なマフラーやストール</li>
                      <li>• 室内での脱ぎ着を考慮</li>
                      <li>• 足元の防寒対策</li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-orange-800 mb-4">
                      <i className="ri-temp-hot-line mr-2"></i>
                      暖かい季節の配慮
                    </h3>
                    <ul className="space-y-2 text-orange-700">
                      <li>• 通気性の良い素材選択</li>
                      <li>• 適度な肌の露出</li>
                      <li>• 汗対策への配慮</li>
                      <li>• 日差し対策</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  国際的なマナーへの配慮
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  グローバルな環境では、異なる文化的背景を持つ方々との交流があります。宗教的な配慮や文化的な慣習を理解し、相手に不快感を与えない装いを心がけることが大切です。
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    執事ベルからのアドバイス
                  </h3>
                  <p className="text-amber-700 leading-relaxed">
                    「ドレスコードの本質は、相手への敬意の表現です。規則を覚えることも大切ですが、最も重要なのは、その場にふさわしい装いで相手に敬意を示すことです。自分らしさを保ちながら、品格ある装いを心がけてください。真の elegance は、内面の美しさから生まれるものなのです。」
                  </p>
                </div>
              </section>
            </div>

            {/* Related Articles */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-2xl font-serif text-gray-900 mb-8">関連記事</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/wisdom/articles/posture" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-posture-41bfba.jpg"
                    alt="美しい所作"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">美しい所作</h4>
                    <p className="text-sm text-gray-600 mt-2">品格ある立ち居振る舞いの基本</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/social-etiquette" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-social2-902b98.jpg"
                    alt="社交のエチケット"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">社交のエチケット</h4>
                    <p className="text-sm text-gray-600 mt-2">パーティーや社交場での振る舞い方</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/table-manners" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-table2-90815a.jpg"
                    alt="テーブルマナーの美学"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">テーブルマナーの美学</h4>
                    <p className="text-sm text-gray-600 mt-2">フォーマルディナーでの作法</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default DressCode;
